import { Box, IconButton } from "@mui/material"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import PauseIcon from "@mui/icons-material/Pause"
import UpdateIcon from "@mui/icons-material/Update"
import { useEffect, useMemo, useRef, useState } from "react"
import { nowMs } from "@/utils/appNow"
import { WW_BLUE } from "@/theme/tokens"

const HOUR_MS = 3600 * 1000
const DAY_MS = 24 * HOUR_MS
// Unterhalb dieser Pill-Breite werden die Tages-Labels auf die Zahl gekürzt,
// darunter trägt nur noch jede N-te Pill ihre Zahl (alle bleiben klickbar)
const DAY_LABEL_MIN_PX = 56
const DAY_NUMBER_MIN_PX = 24
const PLAY_INTERVAL_MS = 1000
// Auswählbar ist nur das 6h-Raster (00/06/12/18/00) — wie die Datengrundlage
const STEP_HOURS = 6
const STEP_MS = STEP_HOURS * HOUR_MS
const HOUR_TICK_LABELS = ["00", "06", "12", "18", "00"]
// Der 24h-Tick ist nur Tagesgrenze; 00 des Folgetags erreicht man über Chevrons/Pfeile/Pills
const LAST_HOUR = 24 - STEP_HOURS
const isBoundaryTick = (i) => i === HOUR_TICK_LABELS.length - 1
// Letztes Drittel des 18–24-Segments gepunktet: läuft in den Folgetag
const DOTTED_FROM_PCT = ((LAST_HOUR + (STEP_HOURS * 2) / 3) / 24) * 100

const clamp = (value, lo, hi) => Math.min(hi, Math.max(lo, value))

const WEEKDAY_FMT = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  timeZone: "UTC",
})
const MONTH_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  timeZone: "UTC",
})

function Chevron({ flip }) {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      style={flip ? { transform: "scaleX(-1)" } : undefined}
    >
      <polyline
        points="6,3 11.5,8.5 6,14"
        fill="none"
        stroke={WW_BLUE}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// Zweistufige Zeitleiste: Day rail (Pills) + Hour scrubber, Readout fest rechts.
// Vertrag nach außen unverändert: setSliderPosition({ value: Stunden-Offset, label: ISO }).
export default function TimeSlider({
  sliderPosition,
  setSliderPosition,
  sliderTicks,
}) {
  const [playing, setPlaying] = useState(false)

  const startMs = Date.parse(sliderTicks[0].label)
  const endMs = Date.parse(sliderTicks[sliderTicks.length - 1].label)

  const days = useMemo(() => {
    const first = new Date(startMs)
    const firstDayStartMs = Date.UTC(
      first.getUTCFullYear(),
      first.getUTCMonth(),
      first.getUTCDate(),
    )
    const out = []
    for (let t = firstDayStartMs; t <= endMs; t += DAY_MS) out.push(t)
    return out
  }, [startMs, endMs])

  // Tages-Labels dünnen sich bei schmalem Viewport auf die Tageszahl aus
  const railRef = useRef(null)
  const [railWidth, setRailWidth] = useState(0)
  useEffect(() => {
    const el = railRef.current
    if (!el) return undefined
    const update = () => setRailWidth(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const pxPerPill =
    railWidth > 0 && days.length > 0 ? railWidth / days.length : Infinity
  const fullDayLabels = pxPerPill >= DAY_LABEL_MIN_PX
  const compact = pxPerPill < DAY_NUMBER_MIN_PX
  const labelEveryN = compact ? Math.ceil(DAY_NUMBER_MIN_PX / pxPerPill) : 1

  const hasPosition = typeof sliderPosition?.label === "string"
  const currentMs = hasPosition
    ? clamp(Date.parse(sliderPosition.label), startMs, endMs)
    : startMs
  const current = new Date(currentMs)
  const hour = current.getUTCHours()
  const dayIndex = clamp(
    Math.floor((currentMs - days[0]) / DAY_MS),
    0,
    days.length - 1,
  )

  const commitMs = (ms) => {
    const clamped = clamp(ms, startMs, endMs)
    if (clamped === currentMs) return
    setSliderPosition({
      value: Math.round((clamped - startMs) / HOUR_MS),
      label: new Date(clamped).toISOString(),
    })
  }
  const commitMsRef = useRef(commitMs)
  commitMsRef.current = commitMs

  useEffect(() => {
    if (!playing) return undefined
    const id = setInterval(() => {
      commitMsRef.current(currentMs >= endMs ? startMs : currentMs + STEP_MS)
    }, PLAY_INTERVAL_MS)
    return () => clearInterval(id)
  }, [playing, currentMs, startMs, endMs])

  const nowRoundedMs = Math.round(nowMs() / STEP_MS) * STEP_MS
  const nowAvailable = nowRoundedMs >= startMs && nowRoundedMs <= endMs
  const isAtNow = currentMs === nowRoundedMs
  const nowDisabled = !nowAvailable || isAtNow

  const pastFraction = clamp((nowMs() - startMs) / (endMs - startMs), 0, 1)

  // ---- Hour scrubber (Pointer-Drag; Listener räumen bei pointerup und Unmount auf) ----
  const trackRef = useRef(null)
  const teardownDragRef = useRef(null)
  useEffect(() => () => teardownDragRef.current?.(), [])

  const hourFromClientX = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect()
    const fraction = clamp((clientX - rect.left) / rect.width, 0, 1)
    return clamp(
      Math.round(fraction * (24 / STEP_HOURS)) * STEP_HOURS,
      0,
      LAST_HOUR,
    )
  }
  const commitHour = (h) => commitMsRef.current(days[dayIndex] + h * HOUR_MS)

  const handleScrubberPointerDown = (e) => {
    e.preventDefault()
    commitHour(hourFromClientX(e.clientX))
    const onMove = (ev) => commitHour(hourFromClientX(ev.clientX))
    const onUp = () => teardownDragRef.current?.()
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    teardownDragRef.current = () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      teardownDragRef.current = null
    }
  }

  const handleScrubberKeyDown = (e) => {
    const steps = { ArrowLeft: -1, ArrowRight: 1, ArrowDown: -1, ArrowUp: 1 }
    if (e.key in steps) {
      e.preventDefault()
      commitMs(currentMs + steps[e.key] * STEP_MS)
    } else if (e.key === "Home") {
      e.preventDefault()
      commitHour(0)
    } else if (e.key === "End") {
      e.preventDefault()
      commitHour(LAST_HOUR)
    }
  }

  if (!hasPosition) return null

  const timeText = `${String(hour).padStart(2, "0")}:00`
  const dateText = `${WEEKDAY_FMT.format(current)} ${current.getUTCDate()} ${MONTH_FMT.format(current)}`
  const nextDayMs = days[dayIndex] + DAY_MS
  const nextDayText =
    nextDayMs <= endMs ? WEEKDAY_FMT.format(new Date(nextDayMs)) : null

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
      {/* Linke Spalte: Transport auf Höhe der Day rail, Readout darunter */}
      <Box sx={{ flexShrink: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          height: "28px",
        }}
      >
        <IconButton
          aria-label={playing ? "pause" : "play"}
          onClick={() => setPlaying((p) => !p)}
          sx={{
            width: 30,
            height: 30,
            bgcolor: WW_BLUE,
            color: "#fff",
            "&:hover": { bgcolor: "#013a5c" },
          }}
        >
          {playing ? (
            <PauseIcon sx={{ fontSize: 15 }} />
          ) : (
            <PlayArrowIcon sx={{ fontSize: 15 }} />
          )}
        </IconButton>
        <IconButton
          aria-label="6 hours back"
          size="small"
          onClick={() => commitMs(currentMs - STEP_HOURS * HOUR_MS)}
        >
          <Chevron flip />
        </IconButton>
        <IconButton
          aria-label="6 hours forward"
          size="small"
          onClick={() => commitMs(currentMs + STEP_HOURS * HOUR_MS)}
        >
          <Chevron />
        </IconButton>
        <Box
          component="button"
          type="button"
          disabled={nowDisabled}
          aria-label="jump to now"
          title={nowAvailable ? "Now" : "Now is outside the slider range"}
          onClick={() => commitMs(nowRoundedMs)}
          sx={{
            p: compact ? "4px 8px" : "4px 13px",
            borderRadius: "999px",
            fontSize: "11.5px",
            fontWeight: 700,
            fontFamily: "inherit",
            cursor: nowDisabled ? "default" : "pointer",
            border: nowDisabled ? "1px solid #e2e8ee" : "1px solid #cfe0ec",
            bgcolor: nowDisabled ? "transparent" : "#eef4f8",
            color: nowDisabled ? "#a8b6c2" : WW_BLUE,
          }}
        >
          {compact ? (
            <UpdateIcon sx={{ fontSize: 16, display: "block" }} />
          ) : (
            "Now"
          )}
        </Box>
      </Box>
      <Box
        sx={{
          mt: "6px",
          fontSize: "17px",
          fontWeight: 700,
          color: WW_BLUE,
          whiteSpace: "nowrap",
        }}
      >
        {timeText}{" "}
        <Box
          component="span"
          sx={{ fontSize: "11.5px", fontWeight: 400, color: "#8a98a6" }}
        >
          UTC
        </Box>{" "}
        <Box
          component="span"
          sx={{ fontSize: "13px", fontWeight: 400, color: "#57728d" }}
        >
          {dateText}
        </Box>
      </Box>
      </Box>

      {/* Day rail + Hour scrubber */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          ref={railRef}
          sx={{
            display: "flex",
            gap: "2px",
            p: "3px",
            borderRadius: "999px",
            bgcolor: "rgba(0,79,125,0.08)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${pastFraction * 100}%`,
              bgcolor: "rgba(0,79,125,0.09)",
              pointerEvents: "none",
            }}
          />
          {days.map((dayStartMs, i) => {
            const active = i === dayIndex
            const d = new Date(dayStartMs)
            const showLabel = active || i % labelEveryN === 0
            const label = fullDayLabels
              ? `${d.getUTCDate()} ${WEEKDAY_FMT.format(d)}`
              : showLabel
                ? String(d.getUTCDate())
                : " "
            return (
              <Box
                key={dayStartMs}
                component="button"
                type="button"
                aria-pressed={active}
                onClick={() => commitMs(dayStartMs + hour * HOUR_MS)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: "center",
                  p: "4px 0",
                  border: 0,
                  borderRadius: "999px",
                  fontSize: "12px",
                  fontFamily: "inherit",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  position: "relative",
                  bgcolor: active ? WW_BLUE : "transparent",
                  color: active ? "#fff" : "#57728d",
                  fontWeight: active ? 700 : 500,
                }}
              >
                {label}
              </Box>
            )
          })}
        </Box>

        <Box
          ref={trackRef}
          role="slider"
          tabIndex={0}
          aria-label="Hour of day"
          aria-valuemin={0}
          aria-valuemax={LAST_HOUR}
          aria-valuenow={hour}
          aria-valuetext={`${timeText} UTC, ${dateText}`}
          onPointerDown={handleScrubberPointerDown}
          onKeyDown={handleScrubberKeyDown}
          sx={{
            position: "relative",
            height: "18px",
            mt: "5px",
            cursor: "pointer",
            touchAction: "none",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "7px",
              left: 0,
              width: `${DOTTED_FROM_PCT}%`,
              height: "4px",
              borderRadius: "2px",
              bgcolor: "#e2e8ee",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "7px",
              left: `${DOTTED_FROM_PCT}%`,
              right: 0,
              height: 0,
              borderTop: "4px dotted #e2e8ee",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "7px",
              left: 0,
              width: `${(hour / 24) * 100}%`,
              height: "4px",
              borderRadius: "2px",
              bgcolor: WW_BLUE,
            }}
          />
          {HOUR_TICK_LABELS.map((_, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: "4px",
                left: `${i * 25}%`,
                transform: "translateX(-50%)",
                width: "1px",
                height: "10px",
                bgcolor: isBoundaryTick(i) ? "#e2e8ee" : "#c4cdd5",
                pointerEvents: "none",
              }}
            />
          ))}
          <Box
            sx={{
              position: "absolute",
              top: "9px",
              left: `${(hour / 24) * 100}%`,
              transform: "translate(-50%, -50%)",
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              bgcolor: "#fff",
              border: `4px solid ${WW_BLUE}`,
              boxSizing: "border-box",
              boxShadow: "0 1px 4px rgba(18,52,86,0.3)",
            }}
          />
        </Box>
        <Box
          sx={{
            position: "relative",
            height: "13px",
            fontSize: "10px",
            color: "#8a98a6",
          }}
        >
          {HOUR_TICK_LABELS.map((label, i) => (
            <Box
              key={i}
              component="span"
              sx={{
                position: "absolute",
                left: `${i * 25}%`,
                transform: isBoundaryTick(i)
                  ? "translateX(-100%)"
                  : "translateX(-50%)",
                whiteSpace: "nowrap",
                color: isBoundaryTick(i) ? "#c4cdd5" : undefined,
              }}
            >
              {isBoundaryTick(i) && nextDayText ? `${nextDayText} ` : ""}
              {label}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
